// ---------------------------------------------------------------------------
// SetupScreen — password creation form (shown when !isConfigured)
// ---------------------------------------------------------------------------

import { useState, type FormEvent } from 'react';

interface SetupScreenProps {
  onSubmit: (password: string) => Promise<void>;
  onSkip: () => void;
}

export function SetupScreen({ onSubmit, onSkip }: SetupScreenProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(password);
    } catch (err) {
      console.error('Setup failed:', err);
      setError('Failed to set up password. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-8 shadow-lg">
        <div className="mb-6 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-white">
            <svg
              className="h-7 w-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        <h1 className="mb-2 text-center text-xl font-bold text-[var(--color-text)]">
          Create a password
        </h1>
        <p className="mb-4 text-center text-sm text-[var(--color-text-secondary)]">
          This password encrypts your data on this device.
        </p>

        <div className="mb-5 rounded-md border border-warning-light bg-[var(--color-warning-light)] p-3">
          <p className="text-xs text-[var(--color-warning)]">
            <strong>No password recovery.</strong> If you forget this password,
            your encrypted data cannot be recovered.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label
              htmlFor="applock-setup-password"
              className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]"
            >
              Password
            </label>
            <input
              id="applock-setup-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-[var(--color-text)] focus:border-border-focus focus:outline-none"
              autoFocus
              disabled={submitting}
            />
          </div>

          <div>
            <label
              htmlFor="applock-setup-confirm"
              className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]"
            >
              Confirm password
            </label>
            <input
              id="applock-setup-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-[var(--color-text)] focus:border-border-focus focus:outline-none"
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="text-xs text-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !password || !confirm}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-[var(--transition-fast)] hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Setting up…' : 'Create password'}
          </button>
        </form>

        <button
          onClick={onSkip}
          disabled={submitting}
          className="mt-3 w-full rounded-md border border-border px-4 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] transition-[var(--transition-fast)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-50"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
